import { useMemo, useState } from "react";
import {
  Box,
  Skeleton,
  Text,
  WidgetEmpty,
  WidgetInteractive,
  useElementSize,
  useIsMobile,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import {
  useOutcomeComparison,
  useOutcomePerformance,
  type OutcomeBreakdownRow,
  type OutcomePerformanceData,
  type OutcomePerformanceUserRow,
} from "./use-outcome-performance";
import { OutcomePerformanceDataPanel } from "./outcome-performance-data-panel";
import { outcomePerformanceTitle } from "../shared/derived-titles";
import { stageExpandedSelection, takeExpandedSelection } from "./expanded-selection";
import {
  type OutcomeActivitySlice,
  outcomePerformanceConfigSchema,
  type OutcomePerformanceConfig,
  type ToneKey,
  TONE_LABELS,
  type OutcomeSelection,
  sameSelection,
  comparisonText,
  rateText,
  totalsText,
} from "./vocabulary";
import { SummaryBody } from "./summary";
import { EntityOutcomeRender } from "./entities";

export * from "./vocabulary";

/** The narrowest card, in grid columns, that shows the previous window's
 *  phrase; narrower cards keep the header's rate but drop it. The seeded
 *  cards are 12 wide. */
const COMPARISON_MIN_W = 8;

/**
 * The card's answer, in the header beside the title: how many Updates, the
 * positive rate they carry ("· 34 Updates · 63% positive") and, fainter, the
 * same rate over the previous window ("vs 52% last week"). It belongs to the
 * WHOLE card rather than to any tone, and the body's Positive cell already
 * holds the rate's figure — a 4xl twin of it beside the mix only restated the
 * legend. The previous window is a secondary read, so its failure costs that
 * phrase alone, never the line. `entity-list` reads its own count in the same
 * slot; Apollo serves the second read of one query from the cache.
 */
function OutcomeTotals({ config, w }: WidgetRenderContext<OutcomePerformanceConfig>) {
  const isMobile = useIsMobile();
  const { data, loading, error } = useOutcomePerformance(
    config.sortBy,
    config.minUpdates,
    config.limit,
  );
  if (error || config.view === "entities" || data.total === 0) return null;
  // A re-query holds the slot with a placeholder rather than emptying it: the
  // line vanishing and returning beside the title is its own flash. Before the
  // first answer the total is zero and nothing is drawn — the line is not yet
  // known to exist.
  if (loading) return <Skeleton delayed height={8} width={80} />;
  const rate = rateText(data);
  // The previous window's phrase, the line's longest part, waits for a wide
  // card. A phone stacks every card full width but keeps its stored `w`, so
  // the phone never shows it.
  const showPrevious = rate !== null && !isMobile && w >= COMPARISON_MIN_W;
  return (
    <Box direction="row" align="baseline" gap="xs" minWidth={0}>
      <Text size="xs" variant="muted" tabular truncate>
        {totalsText(data)}
        {rate ? ` · ${rate}` : null}
      </Text>
      {showPrevious ? <OutcomeComparison /> : null}
    </Box>
  );
}

/** The previous window's phrase. Its own component, so the comparison query
 *  runs only where the phrase can show. */
function OutcomeComparison() {
  const { comparison, loading, error } = useOutcomeComparison();
  if (error) return null;
  if (loading) return <Skeleton delayed height={8} width={72} />;
  if (!comparison) return null;
  return (
    <Box shrink={false}>
      <Text size="xs" variant="faint" tabular>
        {comparisonText(comparison)}
      </Text>
    </Box>
  );
}

/** The drill slice a selection names under the data in hand, or null when the
 * data no longer has a target for it. */
function sliceFor(
  selection: OutcomeSelection,
  data: OutcomePerformanceData,
): OutcomeActivitySlice | null {
  if ("tone" in selection) {
    const count = data[selection.tone];
    // A tone that counts nothing is not a press target, so a tone that
    // stops counting closes rather than paging an empty set under a tile
    // that offers no way to close it.
    if (count === 0) return null;
    return {
      kind: "outcomeActivity",
      outcomeIds: data.outcomeIdsByTone[selection.tone],
      total: count,
      label: TONE_LABELS[selection.tone],
    };
  }
  if ("outcomeId" in selection) {
    const outcome = data.outcomes.find((row) => row.outcomeId === selection.outcomeId);
    // An outcome that left the window has no row to hang the panel on.
    if (!outcome || outcome.count === 0) return null;
    return {
      kind: "outcomeActivity",
      outcomeIds: [outcome.outcomeId],
      total: outcome.count,
      label: outcome.label,
    };
  }
  const row = data.users.find((user) => user.userId === selection.actorId);
  // A member who has left the ranked set has no row to hang the panel on.
  if (!row) return null;
  return {
    kind: "outcomeActivity",
    actorIds: [row.userId],
    total: row.total,
    label: row.label,
  };
}

/** The card body: the per-entity view has its own read, so it is its own
 *  component and the tone/member views keep theirs. */
function OutcomePerformanceRender(context: WidgetRenderContext<OutcomePerformanceConfig>) {
  return context.config.view === "entities" ? (
    <EntityOutcomeRender {...context} />
  ) : (
    <ToneAndMemberRender {...context} />
  );
}

function ToneAndMemberRender({
  expanded,
  config,
}: WidgetRenderContext<OutcomePerformanceConfig>) {
  const { data, loading, error } = useOutcomePerformance(
    config.sortBy,
    config.minUpdates,
    config.limit,
  );
  const scope = useDashboardScope();
  const emptyTitle = useWindowEmptyTitle("No Updates");
  // A press on the card opens the detail dialog's chart-only view with this
  // same selection staged (`expanded-selection.ts`), so the expanded render
  // below mounts already disclosed — the Updates table at dialog size. Inside
  // the dialog (`expanded`) presses toggle in place. Only where no host
  // provides the drilldown channel (the kit's docs demos) does the card
  // disclose the table under its own rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const [selection, setSelection] = useState<OutcomeSelection | null>(() =>
    expanded && instance ? takeExpandedSelection(instance.instanceId) : null,
  );
  const toggle = (next: OutcomeSelection) => {
    if (!expanded && drill && instance) {
      stageExpandedSelection(instance.instanceId, next);
      drill.openChart(instance.instanceId);
      return;
    }
    // Without a host, a second press on the open tone or member closes its
    // table. In the dialog the table IS the view, so the press keeps it: a
    // dialog showing the band over nothing is not a state worth reaching.
    setSelection((current) =>
      !expanded && sameSelection(current, next) ? null : next,
    );
  };
  const openTone = (tone: ToneKey) => toggle({ tone });
  const openOutcome = (row: OutcomeBreakdownRow) => toggle({ outcomeId: row.outcomeId });
  const openMember = (row: OutcomePerformanceUserRow) => toggle({ actorId: row.userId });
  // The slice the selection resolves to under the data in hand. While a
  // re-query is in flight that is the previous answer (the count hook holds
  // it), so the panel stays open and reloads in place; once the new answer
  // lands the slice re-reads from it.
  const activeSlice = useMemo<OutcomeActivitySlice | null>(
    () => (selection ? sliceFor(selection, data) : null),
    [selection, data],
  );

  // The body measures its own height; the summary folds by it.
  const { ref: bodyRef, size: bodySize } = useElementSize<HTMLDivElement>();

  if (error) throw error;
  // Nothing posted in the window: one line naming it, like every empty card
  // on the board. A dash over a bare bar and three zeros spends the card
  // saying the same thing, and the window control is the thing to reach for.
  if (!loading && data.total === 0) {
    return <WidgetEmpty title={emptyTitle} />;
  }
  const content = (
    <SummaryBody
      data={data}
      loading={loading}
      expanded={expanded}
      bodyHeight={bodySize.height}
      tableOpen={activeSlice !== null}
      // A member's own board would list one row restating the header, so
      // the list there is the outcomes instead.
      showMembers={scope?.entity?.type !== "member"}
      selection={selection}
      onTone={openTone}
      onOutcome={openOutcome}
      onMember={openMember}
    />
  );
  return (
    // The card's height is the board's. Nothing here grows the card: the
    // summary takes the spare height only while nothing is disclosed, and a
    // disclosed Updates table takes what remains and scrolls inside it.
    <Box ref={bodyRef} direction="column" grow minHeight={0}>
      {content}
      {activeSlice ? (
        <WidgetInteractive
          direction="column"
          marginTop="sm"
          // The body drops the card's end inset (`body: "bleed-bottom"`), so the
          // island that fills to the card's foot restores one of its own.
          marginBottom="md"
          grow
          minHeight={0}
          rounded="md"
          border
          overflow="hidden"
          // No fill of its own: the table paints the widget surface, and the
          // island reads as one plane only when the toolbar above the rows
          // sits on that same surface.
        >
          <OutcomePerformanceDataPanel
            slice={activeSlice}
            embedded
            fillHeight
          />
        </WidgetInteractive>
      ) : null}
    </Box>
  );
}

const outcomePerformanceSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "view",
        kind: "select",
        label: "View",
        icon: "layout-dashboard",
        options: [
          { value: "summary", label: "Outcome summary" },
          { value: "entities", label: "Projects" },
        ],
      },
      {
        key: "sortBy",
        kind: "select",
        label: "Rank members by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "updates", label: "Update volume" },
          { value: "positive-rate", label: "Positive rate" },
          { value: "completion", label: "Outcome completion" },
        ],
      },
      {
        key: "minUpdates",
        kind: "number",
        label: "Minimum Updates",
        icon: "filter",
        min: 1,
        max: 100,
        step: 1,
      },
      {
        key: "limit",
        kind: "number",
        label: "Max members",
        icon: "list",
        min: 3,
        max: 20,
        step: 1,
      },
    ],
  },
];

export const outcomePerformanceDef: WidgetModule<OutcomePerformanceConfig> = {
  type: "outcome-performance",
  title: "Update outcomes",
  dynamicTitle: (config) => outcomePerformanceTitle(config, config.view),
  icon: "bar-chart-increasing",
  // The card's height is the board's (the standard 3–12 rows): the body folds
  // to it — band alone when short, the table scrolling under the band when
  // tall — and never derives the card's rows from its own content.
  span: { minW: 6, minH: 3, maxW: 12, maxH: 12, defaultW: 8 },
  // Every scroller here is the body's own (the outcome list, the member
  // roster, a disclosed Updates island), so the body drops the card's end
  // inset alone: a list's scroll-end gutter reaches the card's edge as its
  // own content, and a bordered island restores an `md` foot itself.
  body: "bleed-bottom",
  configSchema: outcomePerformanceConfigSchema,
  titleAdornment: OutcomeTotals,
  // The summary discloses its Updates table in the body, so
  // at dialog size a Data drawer would show the table beside itself. The
  // entities view drills to the Data panel from its breakdown, so it keeps it.
  detailPresentation: (config) => (config.view === "entities" ? undefined : "chart"),
  settings: (config) => [
    {
      ...outcomePerformanceSettings[0],
      // The member settings rank the summary's member list; the per-entity
      // view has no members to rank.
      fields:
        config.view === "entities"
          ? outcomePerformanceSettings[0]?.fields.filter((field) => field.key === "view") ?? []
          : outcomePerformanceSettings[0]?.fields ?? [],
    },
  ],
  catalog: [
    {
      key: "outcome-performance.summary",
      label: "Update outcomes",
      description: "The positive rate, the outcomes behind it, and how each member's Updates ended.",
      section: "outcomes" satisfies WidgetSection,
      tags: ["projects", "activity", "outcomes"] satisfies readonly WidgetTag[],
      defaultConfig: { view: "summary" },
      locks: ["view"],
      autoTitle: false,
      defaultW: 8,
    },
    {
      key: "outcome-performance.entities",
      label: "Update outcomes by Project",
      description:
        "Update results for Projects, opening into their outcomes and the Updates behind them.",
      section: "outcomes" satisfies WidgetSection,
      tags: ["projects", "activity", "outcomes"] satisfies readonly WidgetTag[],
      defaultConfig: { view: "entities" },
      locks: ["view"],
      autoTitle: false,
      defaultW: 8,
    },
  ],
  render: OutcomePerformanceRender,
};
